import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

// Profile data interface (shared across all services)
export interface ProfileData {
  energy_profile?: {
    timezone?: string;
    wakeTime?: string;
    [key: string]: any;
  } | Record<string, any> | null;
  [key: string]: any;
}

/**
 * Shared hook for fetching profile data with consistent caching
 * This eliminates duplicate profile API calls across the application
 */
export function useProfileData(userId?: string | null) {
  return useQuery({
    queryKey: ['profile-energy', userId],
    queryFn: async (): Promise<ProfileData | null> => {
      if (!userId || userId === 'debug') {
        return null;
      }

      console.log(`[useProfileData] Fetching profile for ${userId}`);
      
      const { data: fetchedProfile, error: profileError } = await supabase
        .from('profiles')
        .select('energy_profile')
        .eq('id', userId)
        .maybeSingle();
      
      if (profileError) {
        console.error('[useProfileData] Error fetching profile:', profileError);
        return null;
      }
      
      // Process the profile data to ensure energy_profile is properly typed
      const processedProfile: ProfileData | null = fetchedProfile ? {
        ...fetchedProfile,
        energy_profile: typeof fetchedProfile.energy_profile === 'object' 
          ? fetchedProfile.energy_profile 
          : typeof fetchedProfile.energy_profile === 'string'
            ? JSON.parse(fetchedProfile.energy_profile)
            : null
      } : null;
      
      return processedProfile;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: !!userId && userId !== 'debug'
  });
}

/**
 * Helper function to extract wake time from profile as a numeric hour
 */
export function extractWakeTimeFromProfile(profileData: ProfileData | null): number {
  if (profileData && 
      typeof profileData.energy_profile === 'object' && 
      profileData.energy_profile && 
      typeof profileData.energy_profile.wakeTime === 'string') {
    
    const wakeTimeParts = profileData.energy_profile.wakeTime.split(':');
    if (wakeTimeParts.length === 2) {
      const wakeHour = parseInt(wakeTimeParts[0]);
      if (!isNaN(wakeHour) && wakeHour >= 0 && wakeHour <= 23) {
        return wakeHour;
      }
    }
  }
  
  // Default wake time if not found in profile
  return 7;
}